import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Loader2, Download, CheckCircle, AlertCircle, FolderOpen,
  GripVertical, X, Video, Plus, ChevronLeft, ArrowRight
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { UpgradeModal } from '@/components/subscription';
import LibraryPicker, { stagedFileName, type StagedLibraryFile } from '@/components/library/LibraryPicker';
import { useMovConversion } from '@/hooks/useMovConversion';

// File size limit for non-subscribers (10MB)
const MAX_FILE_SIZE_FREE = 10 * 1024 * 1024;

interface VideoFile {
  id: string;
  file: File;
  status: 'ready' | 'uploading' | 'uploaded' | 'processing' | 'processed' | 'error';
  progress: number;
  statusText?: string;
  s3Key?: string;
  thumbnailUrl?: string;
  isImported?: boolean; // true when picked from My Library (already staged in S3 — skip upload)
}

interface CompletedConcat {
  id: string;
  fileName: string;
  downloadUrl: string;
  originalFiles: string[];
  completedAt: Date;
  videoCount: number;
}

// Clean up ugly filenames for display
const cleanFileName = (name: string): string => {
  // Remove common UUID patterns and timestamps
  let cleaned = name
    .replace(/^[a-z0-9]{20,}_/i, '') // Remove leading UUIDs like "7xx6kqrjbxrgc0cw4q38t5vrag_"
    .replace(/_enhanced_audio_\d+/i, '') // Remove "_enhanced_audio_1234567890"
    .replace(/_\d{10,}/g, '') // Remove long numeric timestamps
    .replace(/\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}/g, '') // Remove date-time patterns
    .replace(/__+/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, '') // Remove leading/trailing underscores
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  // Get extension
  const ext = name.match(/\.[^.]+$/)?.[0] || '';

  // If cleaned name is too short or empty, use a truncated version of original
  if (cleaned.length < 3 || cleaned === ext) {
    cleaned = name.replace(ext, '');
  }

  // Truncate if still too long (max 25 chars before extension)
  const nameWithoutExt = cleaned.replace(ext, '');
  if (nameWithoutExt.length > 25) {
    cleaned = nameWithoutExt.slice(0, 22) + '...' + ext;
  } else {
    cleaned = nameWithoutExt + ext;
  }

  return cleaned;
};

const VideoConcatenate = () => {
  const [videos, setVideos] = useState<VideoFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedConcats, setCompletedConcats] = useState<CompletedConcat[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const { toast } = useToast();
  const { isSubscriber, loading: subscriptionLoading } = useSubscription();
  const { getAuthHeaders } = useAuth();
  const { needsConversion, convert } = useMovConversion();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const videoFiles = acceptedFiles.filter(file => file.type.startsWith('video/'));

    if (videoFiles.length !== acceptedFiles.length) {
      toast({
        title: "Some files skipped",
        description: "Only video files are accepted",
        variant: "destructive"
      });
    }

    // Check file size limit for non-subscribers (skip check while loading subscription status)
    if (!subscriptionLoading && !isSubscriber) {
      const oversizedFile = videoFiles.find(f => f.size > MAX_FILE_SIZE_FREE);
      if (oversizedFile) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const newVideos: VideoFile[] = videoFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      status: 'ready',
      progress: 0,
      statusText: 'Ready to upload'
    }));

    setVideos(prev => [...prev, ...newVideos]);
  }, [isSubscriber, subscriptionLoading, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': [] },
    multiple: true
  });

  const removeVideo = (id: string) => {
    setVideos(prev => prev.filter(v => v.id !== id));
  };

  // "Choose from your library" — the staged key is already in S3, so the clip
  // is marked isImported and the processing loop skips its upload.
  const handleLibraryPick = (f: StagedLibraryFile) => {
    const newVideo: VideoFile = {
      id: Math.random().toString(36).substr(2, 9),
      file: new File([], stagedFileName(f), { type: 'video/mp4' }),
      status: 'uploaded',
      progress: 100,
      statusText: 'From your library',
      s3Key: f.key,
      isImported: true,
    };
    setVideos(prev => [...prev, newVideo]);
  };

  const updateVideoState = (id: string, updates: Partial<VideoFile>) => {
    setVideos(prev => prev.map(v => v.id === id ? { ...v, ...updates } : v));
  };

  // Drag and drop reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newVideos = [...videos];
    const draggedVideo = newVideos[draggedIndex];
    newVideos.splice(draggedIndex, 1);
    newVideos.splice(index, 0, draggedVideo);
    setVideos(newVideos);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const uploadToS3 = async (file: File, uploadUrl: string, onProgress: (progress: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (videos.length < 2) {
      toast({
        title: "Not enough videos",
        description: "Please add at least 2 videos to merge",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      toast({
        title: "Processing started",
        description: `Uploading and concatenating ${videos.length} videos...`
      });

      // Upload all videos to S3
      const s3Keys: string[] = [];

      for (let i = 0; i < videos.length; i++) {
        const video = videos[i];

        // Library picks are already staged in S3 — skip the upload
        let finalKey: string;
        if (video.isImported && video.s3Key) {
          finalKey = video.s3Key;
        } else {
          updateVideoState(video.id, {
            status: 'uploading',
            progress: 0,
            statusText: 'Getting upload URL...'
          });

          const uploadResponse = await fetch('/get-upload-url', {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              fileName: video.file.name,
              fileType: video.file.type,
              ephemeral: true
            })
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to get upload URL for ${video.file.name}`);
          }

          const { uploadUrl, key } = await uploadResponse.json();
          finalKey = key;

          updateVideoState(video.id, {
            statusText: `Uploading (${i + 1}/${videos.length})...`,
            s3Key: key
          });

          await uploadToS3(video.file, uploadUrl, (progress) => {
            updateVideoState(video.id, {
              progress,
              statusText: `Uploading... ${progress}%`
            });
          });
        }

        // Convert non-MP4 files (e.g. iPhone MOV) via MediaConvert
        if (needsConversion(video.file.name)) {
          updateVideoState(video.id, { statusText: 'Converting MOV to MP4...' });
          const result = await convert(finalKey);
          finalKey = result.outputKey;
          updateVideoState(video.id, { s3Key: finalKey });
        }

        updateVideoState(video.id, {
          status: 'uploaded',
          progress: 100,
          statusText: 'Uploaded'
        });

        s3Keys.push(finalKey);
      }

      // Update all to processing state
      videos.forEach(v => {
        updateVideoState(v.id, {
          status: 'processing',
          statusText: 'Merging...'
        });
      });

      // Start async concatenation
      const response = await fetch('/api/concatenate', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          videoKeys: s3Keys
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = errorData.details
          ? `${errorData.error}: ${errorData.details}`
          : errorData.error || `Failed to merge videos: ${response.statusText}`;
        throw new Error(errorMsg);
      }

      const startResult = await response.json();
      const jobId = startResult.jobId;

      // Poll for completion
      const statusLabels: Record<string, string> = {
        downloading: 'Downloading clips...',
        probing: 'Analyzing formats...',
        normalizing: 'Normalizing...',
        merging: 'Merging videos...',
        uploading: 'Uploading result...',
      };

      const result = await new Promise<{ downloadUrl: string; fileName: string; videoCount: number }>((resolve, reject) => {
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`/api/concatenate/status/${jobId}`, {
              headers: getAuthHeaders()
            });
            if (!statusRes.ok) {
              clearInterval(pollInterval);
              reject(new Error('Failed to check concatenation status'));
              return;
            }
            const status = await statusRes.json();

            if (status.status === 'completed') {
              clearInterval(pollInterval);
              resolve(status);
            } else if (status.status === 'failed') {
              clearInterval(pollInterval);
              reject(new Error(status.error || 'Concatenation failed'));
            } else {
              // Update progress display
              const label = statusLabels[status.status] || 'Processing...';
              const pct = status.progress || 0;
              videos.forEach(v => {
                updateVideoState(v.id, {
                  statusText: `${label} ${pct}%`
                });
              });
            }
          } catch (err) {
            clearInterval(pollInterval);
            reject(err);
          }
        }, 3000); // Poll every 3 seconds

        // Safety timeout: 30 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          reject(new Error('Concatenation timed out after 30 minutes'));
        }, 30 * 60 * 1000);
      });

      // Update all to processed state
      videos.forEach(v => {
        updateVideoState(v.id, {
          status: 'processed',
          statusText: 'Completed!'
        });
      });

      // Add to completed list
      const completedConcat: CompletedConcat = {
        id: Date.now().toString(),
        fileName: result.fileName,
        downloadUrl: result.downloadUrl,
        originalFiles: videos.map(v => v.file.name),
        completedAt: new Date(),
        videoCount: videos.length
      };

      setCompletedConcats(prev => [completedConcat, ...prev]);

      toast({
        title: "Success!",
        description: `${videos.length} videos have been merged successfully`,
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      videos.forEach(v => {
        updateVideoState(v.id, {
          status: 'error',
          statusText: `Error: ${errorMessage}`
        });
      });
      toast({
        title: "Concatenation failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadVideo = async (downloadUrl: string, fileName: string) => {
    try {
      // Use direct link - simpler and avoids CORS issues with S3
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "The download link may have expired",
        variant: "destructive"
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const totalSize = videos.reduce((sum, v) => sum + v.file.size, 0);

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1440,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2.5"
        style={{ padding: '20px 0 10px', fontSize: 13 }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5"
          style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Merge</span>
      </div>

      {/* Header */}
      <header
        style={{
          padding: '10px 0 28px',
          borderBottom: '1px solid var(--hj-hairline-soft)',
          marginBottom: 28,
        }}
      >
        <div className="mono-label" style={{ marginBottom: 10 }}>Merge</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(36px, 4.6vw, 56px)',
            lineHeight: 1,
            color: 'var(--hj-ink)',
            letterSpacing: '-0.025em',
          }}
        >
          Join clips end&#8209;to&#8209;end.
        </h1>
        <p
          className="m-0"
          style={{
            marginTop: 12,
            color: 'var(--hj-ink-3)',
            fontSize: 14.5,
            maxWidth: 560,
            lineHeight: 1.5,
          }}
        >
          Drop your clips in order, reorder by drag, hit merge. Same resolution / framerate works best.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5">
        {/* Main */}
        <section className="flex flex-col gap-4">
          {/* Drop zone + video list */}
          <form
            onSubmit={handleSubmit}
            style={{
              padding: 22,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <div
              {...getRootProps()}
              className="text-center cursor-pointer transition-colors"
              style={{
                padding: '36px 24px',
                borderRadius: 'var(--hj-r-md)',
                border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                marginBottom: videos.length > 0 ? 16 : 0,
              }}
            >
              <input {...getInputProps()} />
              <div
                className="mx-auto flex items-center justify-center"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-3)',
                  color: 'var(--hj-ink-2)',
                }}
              >
                <FolderOpen size={22} strokeWidth={1.6} />
              </div>
              <p
                className="font-hj-display m-0"
                style={{ marginTop: 14, fontSize: 18, color: 'var(--hj-ink)' }}
              >
                {isDragActive ? 'Drop to add' : 'Drag clips here or click'}
              </p>
              <p
                className="m-0"
                style={{ marginTop: 6, fontSize: 13, color: 'var(--hj-ink-3)' }}
              >
                Videos will be merged in the order shown below.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowLibraryPicker(true)}
              className="inline-flex items-center gap-2"
              style={{
                marginTop: 10,
                marginBottom: videos.length > 0 ? 16 : 0,
                padding: '8px 12px',
                borderRadius: 'var(--hj-r-sm)',
                border: '1px solid var(--hj-hairline)',
                background: 'transparent',
                color: 'var(--hj-ink-2)',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              <FolderOpen size={14} strokeWidth={1.8} />
              Choose from your library
            </button>

            {/* Video List */}
            {videos.length > 0 && (
              <div style={{ marginTop: 0 }}>
                <div
                  className="flex items-center justify-between"
                  style={{ marginBottom: 10 }}
                >
                  <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>
                    {videos.length} clip{videos.length === 1 ? '' : 's'} · drag to reorder
                  </div>
                  <button
                    type="button"
                    onClick={() => setVideos([])}
                    className="inline-flex items-center gap-1.5"
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      background: 'transparent',
                      border: '1px solid var(--hj-hairline)',
                      color: 'var(--hj-ink-3)',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Clear all
                  </button>
                </div>

                <div className="space-y-2">
                  {videos.map((video, index) => {
                    const isError = video.status === 'error';
                    const isDragging = draggedIndex === index;
                    return (
                      <div
                        key={video.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center gap-3 transition-all"
                        style={{
                          padding: 12,
                          borderRadius: 'var(--hj-r-sm)',
                          background: isError ? 'oklch(0.30 0.12 25 / 0.18)' : 'var(--hj-surface-2)',
                          border: `1px solid ${
                            isError
                              ? 'hsl(var(--destructive))'
                              : isDragging
                                ? 'var(--hj-blue)'
                                : 'var(--hj-hairline)'
                          }`,
                          opacity: isDragging ? 0.5 : 1,
                          cursor: isProcessing ? 'default' : 'grab',
                        }}
                      >
                        <GripVertical size={16} style={{ color: 'var(--hj-ink-4)' }} />
                        <div
                          className="flex items-center justify-center flex-shrink-0 tabular"
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 999,
                            background: 'var(--hj-blue-tint)',
                            color: 'var(--hj-yellow-ink)',
                            fontFamily: 'var(--hj-font-mono)',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </div>
                        <Video size={16} style={{ color: 'var(--hj-ink-3)' }} />
                        <div className="flex-1 min-w-0">
                          <p
                            className="truncate m-0"
                            style={{ fontSize: 13, color: 'var(--hj-ink)', fontWeight: 500 }}
                          >
                            {video.file.name}
                          </p>
                          <div
                            className="flex items-center gap-2"
                            style={{ marginTop: 2, fontSize: 11 }}
                          >
                            <span style={{ color: 'var(--hj-ink-4)' }}>
                              {video.file.size > 0 ? formatFileSize(video.file.size) : 'Library'}
                            </span>
                            <span style={{ color: 'var(--hj-ink-4)' }}>•</span>
                            <span
                              style={{
                                color: isError
                                  ? 'hsl(var(--destructive))'
                                  : video.status === 'processed'
                                    ? 'var(--hj-green)'
                                    : 'var(--hj-ink-3)',
                              }}
                            >
                              {video.statusText || video.status}
                            </span>
                          </div>
                          {video.progress > 0 &&
                            video.status !== 'processed' &&
                            video.status !== 'error' && (
                              <Progress value={video.progress} className="h-1 mt-1.5" />
                            )}
                        </div>
                        {video.status === 'processed' && (
                          <CheckCircle size={16} style={{ color: 'var(--hj-green)' }} />
                        )}
                        <button
                          type="button"
                          onClick={() => removeVideo(video.id)}
                          disabled={isProcessing}
                          aria-label="Remove"
                          style={{
                            background: 'transparent',
                            border: 0,
                            color: 'var(--hj-ink-4)',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            padding: 4,
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div
                  {...getRootProps()}
                  className="flex items-center justify-center gap-2 cursor-pointer transition-colors"
                  style={{
                    marginTop: 10,
                    padding: 10,
                    borderRadius: 'var(--hj-r-sm)',
                    border: '1px dashed var(--hj-hairline)',
                    background: 'transparent',
                    color: 'var(--hj-ink-3)',
                    fontSize: 12.5,
                  }}
                >
                  <Plus size={14} /> Add more videos
                </div>
              </div>
            )}

            {videos.length >= 2 && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-2)',
                  border: '1px solid var(--hj-hairline-soft)',
                  fontSize: 12.5,
                  color: 'var(--hj-ink-3)',
                  lineHeight: 1.5,
                }}
              >
                Clips with matching resolution / framerate stream-copy without re&#8209;encoding (fast).
                Mixed formats fall back to a full re&#8209;encode.
              </div>
            )}

            {/* Submit */}
            <div className="flex items-center justify-end" style={{ marginTop: 18, gap: 12 }}>
              <button
                type="submit"
                disabled={videos.length < 2 || isProcessing}
                className="inline-flex items-center gap-2 transition-opacity disabled:cursor-not-allowed"
                style={{
                  padding: '11px 22px',
                  borderRadius: 999,
                  background: 'var(--hj-blue)',
                  border: '1px solid var(--hj-blue)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: videos.length < 2 || isProcessing ? 'not-allowed' : 'pointer',
                  opacity: videos.length < 2 || isProcessing ? 0.5 : 1,
                  minHeight: 44,
                }}
              >
                {isProcessing ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Merging…
                  </>
                ) : (
                  <>
                    Merge {videos.length || ''} {videos.length === 1 ? 'clip' : 'clips'}
                    <ArrowRight size={14} strokeWidth={2.4} />
                  </>
                )}
              </button>
            </div>

            {/* Error */}
            {error && (
              <div
                className="flex items-start gap-2"
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'oklch(0.30 0.12 25 / 0.18)',
                  border: '1px solid hsl(var(--destructive) / 0.5)',
                  color: 'hsl(var(--destructive))',
                  fontSize: 13,
                }}
              >
                <AlertCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}
          </form>

          {/* Session History */}
          {completedConcats.length > 0 && (
            <div
              style={{
                padding: 22,
                borderRadius: 'var(--hj-r-md)',
                background: 'var(--hj-surface-1)',
                border: '1px solid var(--hj-hairline)',
              }}
            >
              <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>
                  Session history
                </div>
                <button
                  type="button"
                  onClick={() => setCompletedConcats([])}
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--hj-hairline)',
                    color: 'var(--hj-ink-3)',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              </div>
              <div className="space-y-2">
                {completedConcats.map((concat) => (
                  <div
                    key={concat.id}
                    className="flex items-center gap-3"
                    style={{
                      padding: 12,
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'oklch(0.42 0.15 145 / 0.10)',
                      border: '1px solid oklch(0.42 0.15 145 / 0.45)',
                    }}
                  >
                    <CheckCircle size={16} style={{ color: 'var(--hj-green)' }} />
                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate m-0"
                        style={{ fontSize: 13, color: 'var(--hj-ink)', fontWeight: 500 }}
                      >
                        {concat.fileName}
                      </p>
                      <div
                        className="truncate"
                        style={{ marginTop: 2, fontSize: 11, color: 'var(--hj-ink-3)' }}
                      >
                        {concat.originalFiles.slice(0, 3).map(cleanFileName).join(' · ')}
                        {concat.originalFiles.length > 3 &&
                          ` +${concat.originalFiles.length - 3}`}
                        <span style={{ marginLeft: 8, color: 'var(--hj-ink-4)' }}>
                          {concat.completedAt.toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => downloadVideo(concat.downloadUrl, concat.fileName)}
                      className="inline-flex items-center gap-1.5"
                      style={{
                        padding: '7px 14px',
                        borderRadius: 999,
                        background: 'var(--hj-blue)',
                        border: '1px solid var(--hj-blue)',
                        color: 'oklch(0.18 0.005 70)',
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Download size={13} />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Stats sidebar */}
        <aside className="flex flex-col gap-3">
          <StatTile label="Clips added" value={videos.length} />
          <StatTile
            label="Uploaded"
            value={videos.filter(v => v.status === 'uploaded' || v.status === 'processed').length}
          />
          <StatTile label="Completed merges" value={completedConcats.length} />
          <StatTile label="Total size" value={formatFileSize(totalSize)} />
        </aside>
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        trigger="video-tools"
      />

      <LibraryPicker
        open={showLibraryPicker}
        onClose={() => setShowLibraryPicker(false)}
        onPick={handleLibraryPick}
      />
    </main>
  );
};

const StatTile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div
    style={{
      padding: 16,
      borderRadius: 'var(--hj-r-md)',
      background: 'var(--hj-surface-1)',
      border: '1px solid var(--hj-hairline)',
    }}
  >
    <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{label}</div>
    <div
      className="tabular"
      style={{
        marginTop: 6,
        fontSize: 26,
        fontFamily: 'var(--hj-font-mono)',
        fontWeight: 600,
        color: 'var(--hj-ink)',
        lineHeight: 1,
      }}
    >
      {value}
    </div>
  </div>
);

export default VideoConcatenate;
